import React, { useEffect, useState } from "react";
import {
    Box, Card, CardContent, CardHeader,
    Typography, Stack, Divider,
    TextField, IconButton, Grid, Chip
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import {
    collection, getDocs, addDoc,
    updateDoc, deleteDoc, doc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebase";
import Swal from "sweetalert2";

const colors = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505'
};

function Amenities() {
    const auth = getAuth();

    const [categories, setCategories] = useState([]);
    const [newCategory, setNewCategory] = useState("");
    const [newItem, setNewItem] = useState({});
    const [loading, setLoading] = useState(true);

    const [userData, setUserData] = useState(null);
    const [propertyId, setPropertyId] = useState(null);

    /* ================= AUTH ================= */
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setUserData(user);

                const roleSnap = await getDocs(collection(db, "authroles"));
                roleSnap.forEach(d => {
                    if (d.id === user.uid) {
                        setPropertyId(d.data().propertyId);
                    }
                });
            }
        });

        return () => unsub();
    }, []);

    /* ================= FETCH ================= */
    const fetchCategories = async () => {
        if (!userData || !propertyId) return;

        const snap = await getDocs(collection(db, "amenity_categories"));

        const list = snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(
                c => c.userId === userData.uid && c.propertyId === propertyId
            );

        setCategories(list);
        setLoading(false);
    };

    useEffect(() => {
        fetchCategories();
    }, [userData, propertyId]);

    /* ================= ADD CATEGORY ================= */
    const addCategory = async () => {
        if (!newCategory.trim() || !userData || !propertyId) return;

        await addDoc(collection(db, "amenity_categories"), {
            title: newCategory.trim(),
            items: [],
            userId: userData.uid,
            propertyId: propertyId,
            createdAt: new Date()
        });

        Swal.fire({
            title: "Added",
            text: "Amenity category created",
            icon: "success",
            background: colors.calPolyGreen,
            color: colors.vanilla,
            confirmButtonColor: colors.goldenrod
        });
        setNewCategory("");
        fetchCategories();
    };

    /* ================= DELETE CATEGORY ================= */
    const deleteCategory = async (id) => {
        const ok = await Swal.fire({
            title: "Delete Category?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete",
            background: colors.calPolyGreen,
            color: colors.vanilla,
            confirmButtonColor: colors.goldenrod,
            cancelButtonColor: colors.darkGreen
        });

        if (ok.isConfirmed) {
            await deleteDoc(doc(db, "amenity_categories", id));
            Swal.fire({
                title: "Deleted",
                text: "Category removed",
                icon: "success",
                background: colors.calPolyGreen,
                color: colors.vanilla,
                confirmButtonColor: colors.goldenrod
            });
            fetchCategories();
        }
    };

    /* ================= ADD SUB AMENITY ================= */
    const addSubAmenity = async (cat) => {
        const value = newItem[cat.id];
        if (!value?.trim()) return;

        const updated = [...(cat.items || []), value.trim()];

        await updateDoc(doc(db, "amenity_categories", cat.id), {
            items: updated
        });

        setNewItem({ ...newItem, [cat.id]: "" });
        fetchCategories();
    };

    /* ================= DELETE SUB AMENITY ================= */
    const deleteSubAmenity = async (cat, item) => {
        const updated = cat.items.filter(i => i !== item);

        await updateDoc(doc(db, "amenity_categories", cat.id), {
            items: updated
        });

        fetchCategories();
    };

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }}>
            <Box component="main" sx={{ flexGrow: 1, maxWidth: "1600px", margin: "0 auto", width: "100%" }}>
                <Typography variant="h5" fontWeight={700} mb={3} sx={{ color: colors.vanilla }}>
                    Amenities Master Management
                </Typography>

                {/* ================= ADD MAIN CATEGORY ================= */}
                <Card sx={{ mb: 4, borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
                    <Divider sx={{ borderColor: colors.goldenrod, opacity: 0.4 }} />
                    <CardContent sx={{ p: 3 }}>
                        <Box display="flex" gap={1}>
                            <TextField
                                fullWidth
                                label="Room Amenities / Hotel Amenities"
                                size="small"
                                value={newCategory}
                                onChange={(e) => setNewCategory(e.target.value)}
                                InputLabelProps={{ style: { color: colors.vanilla } }}
                                InputProps={{ style: { color: colors.vanilla } }}
                                sx={{ 
                                    "& .MuiOutlinedInput-root": { 
                                        borderRadius: "8px",
                                        backgroundColor: colors.darkGreen,
                                        "& fieldset": { borderColor: colors.goldenrod },
                                        "&:hover fieldset": { borderColor: colors.gold },
                                        "&.Mui-focused fieldset": { borderColor: colors.gold }
                                    } 
                                }}
                            />
                            <IconButton 
                                onClick={addCategory}
                                sx={{ 
                                    bgcolor: colors.goldenrod, 
                                    color: colors.darkGreen, 
                                    borderRadius: "8px",
                                    '&:hover': { bgcolor: colors.gold } 
                                }}
                            >
                                <Add />
                            </IconButton>
                        </Box>
                    </CardContent>
                </Card>

                {/* ================= CATEGORY LIST ================= */}
                {loading && <Typography sx={{ color: colors.vanilla }}>Loading...</Typography>}

                <div className="container-fluid p-0">
                    <div className="row">
                        {categories.map(cat => (
                            <div className="col-sm-4 mb-3" key={cat.id}>
                                <Card
                                    sx={{
                                        borderRadius: 3,
                                        height: "100%",
                                        border: `1px solid ${colors.goldenrod}`,
                                        bgcolor: colors.calPolyGreen,
                                        color: colors.vanilla,
                                        boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)",
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    <CardHeader
                                        title={<Typography fontWeight={700} sx={{ color: colors.gold }}>{cat.title}</Typography>}
                                        action={
                                            <IconButton
                                                onClick={() => deleteCategory(cat.id)}
                                                color="error"
                                                size="small"
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        }
                                        sx={{ pb: 1 }}
                                    />
                                    <Divider sx={{ borderColor: colors.goldenrod, opacity: 0.4 }} />

                                    <CardContent sx={{ p: 3, flexGrow: 1 }}>
                                        {/* Add Sub Amenity */}
                                        <Box display="flex" gap={1} mb={2}>
                                            <TextField
                                                size="small"
                                                label="Add Sub Amenity"
                                                value={newItem[cat.id] || ""}
                                                onChange={(e) =>
                                                    setNewItem({ ...newItem, [cat.id]: e.target.value })
                                                }
                                                fullWidth
                                                InputLabelProps={{ style: { color: colors.vanilla } }}
                                                InputProps={{ style: { color: colors.vanilla } }}
                                                sx={{ 
                                                    "& .MuiOutlinedInput-root": { 
                                                        borderRadius: "8px",
                                                        backgroundColor: colors.darkGreen,
                                                        "& fieldset": { borderColor: colors.goldenrod },
                                                        "&:hover fieldset": { borderColor: colors.gold },
                                                        "&.Mui-focused fieldset": { borderColor: colors.gold }
                                                    } 
                                                }}
                                            />
                                            <IconButton
                                                onClick={() => addSubAmenity(cat)}
                                                sx={{ 
                                                    bgcolor: colors.goldenrod, 
                                                    color: colors.darkGreen, 
                                                    borderRadius: "8px",
                                                    height: "40px",
                                                    width: "40px",
                                                    '&:hover': { bgcolor: colors.gold } 
                                                }}
                                            >
                                                <Add fontSize="small" />
                                            </IconButton>
                                        </Box>

                                        {/* Sub Amenities */}
                                        <Stack direction="row" flexWrap="wrap" gap={1}>
                                            {(cat.items || []).map(item => (
                                                <Chip
                                                    key={item}
                                                    label={item}
                                                    onDelete={() => deleteSubAmenity(cat, item)}
                                                    sx={{ 
                                                        bgcolor: colors.darkGreen, 
                                                        color: colors.vanilla, 
                                                        border: `1px solid ${colors.goldenrod}`,
                                                        '& .MuiChip-deleteIcon': { color: colors.goldenrod, '&:hover': { color: colors.gold } }
                                                    }}
                                                />
                                            ))}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </div>
                        ))}
                    </div>
                </div>
            </Box>
        </Box>
    );
}

export default Amenities;